import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { smmSettingsApi } from '../lib/api';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { SlidersHorizontal, Save, KeyRound, AlertTriangle, CheckCircle2, Info, Loader2, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

/*
 * SMM panel settings - the Aurora replacement for the settings half of the legacy
 * /orderManagement/accountSettings.
 *
 * Three things shape this page.
 *
 * 1. The API key is write-only. The server never sends it back, only a masked hint, so the field
 *    starts EMPTY and empty means "keep the current key". The page has to say that clearly or an
 *    operator will assume a blank box means the key was lost.
 *
 * 2. The services grid is the highest-consequence field in the product. One malformed line stops
 *    delivery for every service on the account, and a wrong-length settings block makes real
 *    accounts post the wrong text publicly. So it is validated as you type, against the same
 *    server-side validator that guards the save, and errors block saving.
 *
 * 3. "Process orders" is a live kill switch. Turning it off stops the cron for this account, which
 *    is exactly what you want mid-incident - so it is prominent and its state is stated in words,
 *    not just a toggle position.
 */

const FIELD_LABEL = {
  line: 'whole line',
  serviceId: 'service id',
  slots: 'slots',
  targetActionId: 'action',
  speed: 'speed',
  maxDeliveryHours: 'max delivery hours',
  percentExtraDelivery: 'extra delivery %',
  minExtraDelivery: 'min extra delivery',
  orderRecordsRetentionDays: 'retention days',
  ordersPerSession: 'orders per session',
  origami: 'settings block',
};

function IssueList({ issues }) {
  if (!issues || issues.length === 0) return null;
  const errors = issues.filter(i => i.severity === 'ERROR');
  const warnings = issues.filter(i => i.severity !== 'ERROR');
  return (
    <ul className="mt-2 space-y-1.5" data-testid="grid-issues">
      {[...errors, ...warnings].map((i, n) => (
        <li
          key={n}
          className={cn(
            'flex items-start gap-2 rounded-lg px-2.5 py-1.5 text-xs',
            i.severity === 'ERROR' ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning'
          )}
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <span className="font-medium">line {i.line}</span>
            {i.field && <span className="opacity-80"> · {FIELD_LABEL[i.field] || i.field}</span>}
            {' — '}
            {i.message}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function SmmSettingsPage() {
  usePageTitle('SMM Settings');
  const queryClient = useQueryClient();

  const settingsQ = useQuery({
    queryKey: ['smm-settings'],
    queryFn: () => smmSettingsApi.get().then(r => r.data),
  });

  const [form, setForm] = useState(null);
  const [newKey, setNewKey] = useState('');
  const [liveIssues, setLiveIssues] = useState(null);
  const [checking, setChecking] = useState(false);

  // seed the form once the server answers; never overwrite what is being typed
  useEffect(() => {
    if (settingsQ.data && form === null) {
      setForm({
        enable_smm_feature: !!settingsQ.data.enable_smm_feature,
        enable_smm_order_processing: !!settingsQ.data.enable_smm_order_processing,
        smm_panel_starting_url: settingsQ.data.smm_panel_starting_url || '',
        smm_services_settings_grid: settingsQ.data.smm_services_settings_grid || '',
        order_records_retention_days: settingsQ.data.order_records_retention_days ?? '',
        no_of_completed_orders_to_display: settingsQ.data.no_of_completed_orders_to_display ?? '',
      });
      setLiveIssues(settingsQ.data.grid_issues || []);
    }
  }, [settingsQ.data, form]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // WHICH fields the customer may read but not write is the server's call, not the page's. It
  // publishes `read_only` on the GET; we disable exactly those. Hard-coding the list here is how
  // the two drift apart - and the page losing track of it is the whole reason enable_smm_feature
  // shipped as an editable switch in the first place.
  const readOnly = useMemo(
    () => new Set(settingsQ.data?.read_only || []),
    [settingsQ.data]
  );
  const featureLocked = readOnly.has('enable_smm_feature');

  // Validate the grid server-side as it is edited, debounced. Same validator the save uses, so the
  // page can never disagree with what the server will accept.
  useEffect(() => {
    if (!form) return;
    const grid = form.smm_services_settings_grid;
    setChecking(true);
    const t = setTimeout(() => {
      smmSettingsApi
        .validateGrid(grid)
        .then(r => setLiveIssues(r.data.issues || []))
        .catch(() => { /* keep the last known issues rather than clearing them on a blip */ })
        .finally(() => setChecking(false));
    }, 500);
    return () => { clearTimeout(t); setChecking(false); };
  }, [form?.smm_services_settings_grid]);   // eslint-disable-line react-hooks/exhaustive-deps

  const errorCount = useMemo(
    () => (liveIssues || []).filter(i => i.severity === 'ERROR').length,
    [liveIssues]
  );
  const warningCount = (liveIssues || []).length - errorCount;

  const saveMut = useMutation({
    mutationFn: () => {
      const body = { ...form };
      // Do not send what the server will not honour. It drops these keys anyway (AdminOnlyFields),
      // so this is politeness rather than protection - but it keeps the save response's `ignored`
      // list meaningful: anything reported there came from a stale bundle, which is worth seeing.
      readOnly.forEach(k => { delete body[k]; });
      // blank means "keep the stored key" - only send it when the operator typed a new one
      if (newKey.trim()) body.smm_panel_api_key = newKey.trim();
      return smmSettingsApi.save(body);
    },
    onSuccess: (res) => {
      toast.success(res.data?.api_key_changed ? 'Settings saved · API key updated' : 'Settings saved');
      if (res.data?.ignored_note) toast.warning(res.data.ignored_note);
      setNewKey('');
      queryClient.invalidateQueries({ queryKey: ['smm-settings'] });
    },
    onError: (err) => {
      const d = err?.response?.data;
      if (d?.grid_issues) setLiveIssues(d.grid_issues);
      const detail = d?.problems?.join('; ') || d?.error || err?.message || 'unknown error';
      toast.error(`Not saved: ${detail}`);
    },
  });

  if (settingsQ.isLoading || !form) {
    return (
      <div data-testid="smm-settings-loading">
        <PageHeader icon={SlidersHorizontal} title="SMM Settings" description="Loading…" />
        <div className="space-y-3">
          {[0, 1, 2].map(i => <div key={i} className="h-28 animate-pulse rounded-xl bg-secondary/60" />)}
        </div>
      </div>
    );
  }

  if (settingsQ.isError) {
    return (
      <div>
        <PageHeader icon={SlidersHorizontal} title="SMM Settings" />
        <div className="surface flex items-center gap-2 p-4 text-danger">
          <AlertTriangle className="h-4 w-4" />
          <span className="text-sm">Could not load settings. {String(settingsQ.error?.message || '')}</span>
        </div>
      </div>
    );
  }

  const s = settingsQ.data;

  return (
    <div data-testid="smm-settings-page">
      <PageHeader
        icon={SlidersHorizontal}
        title="SMM Settings"
        description="How this account connects to your SMM panel, and which services it runs."
        actions={
          <Button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending || errorCount > 0}
            title={errorCount > 0 ? 'Fix the errors in the services grid first' : 'Save settings'}
            data-testid="smm-settings-save"
          >
            {saveMut.isPending
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <Save className="mr-2 h-4 w-4" />}
            Save
          </Button>
        }
      />

      <div className="space-y-4">
        {/* --- the kill switch, first, because it is what you reach for in an incident --- */}
        <Card className="surface">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Order processing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <Label htmlFor="proc" className="text-sm font-medium">Process orders</Label>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {form.enable_smm_order_processing
                    ? 'ON — the server pulls orders from your panel and assigns them to slots.'
                    : 'OFF — no orders are pulled, assigned or reported for this account.'}
                </p>
              </div>
              <Switch
                id="proc"
                checked={form.enable_smm_order_processing}
                onCheckedChange={v => set('enable_smm_order_processing', v)}
                data-testid="toggle-order-processing"
              />
            </div>
            {/* An ENTITLEMENT, not a setting. V1 only ever reads this flag on the customer side -
                it gates the SMM link (bot-settings.html:249) and this whole page's V1 ancestor
                (boosting-order.html:124-350) - and its sole writer is the ROLE_ADMIN route. It is
                shown rather than hidden so an operator can see what they have; disabled, because
                granting it is the provider's decision. Same shape as the slot and device pause,
                where the admin lever stayed admin-only and the customer got their own field. */}
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <Label htmlFor="feat" className="text-sm font-medium flex items-center gap-1.5">
                  SMM feature
                  {featureLocked && <Lock className="h-3 w-3 text-muted-foreground" aria-hidden />}
                </Label>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {featureLocked
                    ? (form.enable_smm_feature
                        ? 'Enabled for this account by your provider. Contact them to have it removed.'
                        : 'Not enabled for this account. Contact your provider to have it added.')
                    : 'Shows the SMM pages for this account.'}
                </p>
              </div>
              <Switch
                id="feat"
                checked={form.enable_smm_feature}
                disabled={featureLocked}
                aria-readonly={featureLocked || undefined}
                onCheckedChange={featureLocked ? undefined : (v => set('enable_smm_feature', v))}
                title={featureLocked ? 'Set by your provider' : undefined}
                data-testid="toggle-smm-feature"
              />
            </div>
          </CardContent>
        </Card>

        {/* --- panel connection --- */}
        <Card className="surface">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Panel connection</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="url" className="text-sm">Panel API URL</Label>
              <Input
                id="url"
                value={form.smm_panel_starting_url}
                onChange={e => set('smm_panel_starting_url', e.target.value)}
                placeholder="https://your-panel.com/adminapi/v2"
                className="mt-1.5 font-mono text-sm"
                data-testid="input-panel-url"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                A URL containing <code className="text-foreground">v2</code> selects the v2 order pipeline.
              </p>
            </div>

            <div>
              <Label htmlFor="key" className="flex items-center gap-1.5 text-sm">
                <KeyRound className="h-3.5 w-3.5" />
                API key
              </Label>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                {s.api_key_configured ? (
                  <Badge variant="outline" className="border-success/40 text-success" data-testid="api-key-hint">
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    {s.api_key_hint}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-warning/40 text-warning">no key set</Badge>
                )}
              </div>
              <Input
                id="key"
                type="password"
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                placeholder={s.api_key_configured ? 'Leave blank to keep the current key' : 'Paste the panel API key'}
                className="mt-2 font-mono text-sm"
                autoComplete="off"
                data-testid="input-api-key"
              />
              <p className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3 w-3 shrink-0" />
                The key is never sent back to the browser — only the masked hint above. Leaving this
                blank keeps the key you already have; it is not cleared.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* --- the dangerous one --- */}
        <Card className="surface">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">Services grid</CardTitle>
              <div className="flex items-center gap-2">
                {checking && <span className="text-xs text-muted-foreground">checking…</span>}
                {!checking && errorCount === 0 && warningCount === 0 && (
                  <Badge variant="outline" className="border-success/40 text-success" data-testid="grid-ok">
                    <CheckCircle2 className="mr-1 h-3 w-3" />valid
                  </Badge>
                )}
                {errorCount > 0 && (
                  <Badge variant="outline" className="border-danger/40 text-danger" data-testid="grid-errors">
                    {errorCount} error{errorCount > 1 ? 's' : ''}
                  </Badge>
                )}
                {warningCount > 0 && (
                  <Badge variant="outline" className="border-warning/40 text-warning">
                    {warningCount} warning{warningCount > 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={form.smm_services_settings_grid}
              onChange={e => set('smm_services_settings_grid', e.target.value)}
              spellCheck={false}
              rows={10}
              className={cn(
                'font-mono text-xs leading-relaxed',
                errorCount > 0 && 'border-danger/50 focus-visible:ring-danger/30'
              )}
              placeholder="14 | 1-8, 10, 11 | viewViaUrlWithLikeWithSaveWithComment | fast | 72 | 0 | 0 | 30 | 5 | RECENT, 5, 100,100, …"
              data-testid="input-services-grid"
            />
            <p className="mt-2 text-xs text-muted-foreground">
              One service per line:{' '}
              <code className="text-foreground">
                serviceId | slots | action | speed | maxDeliveryHours | extra% | minExtra | retentionDays | ordersPerSession | settings
              </code>
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              The settings block must hold exactly 28 values (29 for repost services). The worker reads
              the comment as the last value of the line, so a wrong count makes it post the wrong thing.
            </p>
            <IssueList issues={liveIssues} />
            {errorCount > 0 && (
              <p className="mt-3 rounded-lg bg-danger/10 px-3 py-2 text-xs text-danger">
                Saving is blocked while there are errors. A malformed line stops delivery for
                <strong> every</strong> service on this account, not just the broken one.
              </p>
            )}
          </CardContent>
        </Card>

        {/* --- housekeeping --- */}
        <Card className="surface">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Records</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="ret" className="text-sm">Keep completed orders for (days)</Label>
              <Input
                id="ret"
                inputMode="numeric"
                value={form.order_records_retention_days}
                onChange={e => set('order_records_retention_days', e.target.value)}
                className="mt-1.5"
                data-testid="input-retention"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Completed orders are deleted after this. Open orders are never pruned.
              </p>
            </div>
            <div>
              <Label htmlFor="disp" className="text-sm">Completed orders to show</Label>
              <Input
                id="disp"
                inputMode="numeric"
                value={form.no_of_completed_orders_to_display}
                onChange={e => set('no_of_completed_orders_to_display', e.target.value)}
                className="mt-1.5"
                data-testid="input-completed-to-show"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                How many settled orders appear on the SMM Orders page alongside the open ones.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
